"use client";

import * as React from "react";
import { toast } from "sonner";
import { Search, ShieldCheck, User as UserIcon } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { faNum, faDate } from "@/lib/format";
import type { User } from "./types";

export default function UsersTable() {
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/admin/users", { cache: "no-store" });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || "خطا در دریافت کاربران");
        }
        if (!cancelled) setUsers(data.users ?? []);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "خطا در دریافت کاربران";
        if (!cancelled) {
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = React.useMemo(() => {
    const q = query.trim();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.includes(q) ||
        u.phone?.includes(q) ||
        (u.role === "ADMIN" && "ادمین".includes(q)),
    );
  }, [users, query]);

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserIcon className="size-4 text-primary" />
            کاربران
            <Badge variant="secondary" className="text-xs">
              {faNum(filtered.length)} نفر
            </Badge>
          </CardTitle>
          <div className="relative">
            <Search className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              dir="ltr"
              placeholder="جستجو بر اساس نام یا تلفن"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 w-56 pr-9 text-left text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            کاربری یافت نشد.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="pr-4">نام</TableHead>
                  <TableHead>تلفن</TableHead>
                  <TableHead>نقش</TableHead>
                  <TableHead>تاریخ عضویت</TableHead>
                  <TableHead className="text-center">پرداخت‌ها</TableHead>
                  <TableHead className="text-center">اشتراک‌ها</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="pr-4">
                      <div className="flex items-center gap-2">
                        <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <UserIcon className="size-4" />
                        </span>
                        <span className="font-medium">{u.name || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell dir="ltr" className="text-sm">
                      {u.phone}
                    </TableCell>
                    <TableCell>
                      {u.role === "ADMIN" ? (
                        <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
                          <ShieldCheck className="size-3" />
                          ادمین
                        </Badge>
                      ) : (
                        <Badge variant="secondary">کاربر</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {faDate(u.createdAt)}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium">{faNum(u._count.payments)}</span>
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium">
                        {faNum(u._count.subscriptions)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
